import { useAuth as useClerkAuth, useSignIn } from '@clerk/clerk-react';
import { isClerkAPIResponseError } from '@clerk/clerk-react/errors';
import type { UserLogin } from '@uturn/api';
import type { User } from '@uturn/api/v1';
import { metaData as fetchUserMetaData } from '@uturn/api/v1';
import { createContext, type FC, type PropsWithChildren } from 'react';

export type Auth = {
	isLoaded: boolean;
	isAuthenticated: boolean;
	login: (credentials: UserLogin) => Promise<User | undefined>;
	logout: () => Promise<void>;
};

export const AuthContext = createContext<Auth | undefined>(undefined);

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
	const { isLoaded, isSignedIn, signOut } = useClerkAuth();
	const { signIn, setActive, isLoaded: isSignInLoaded } = useSignIn();

	const signInAndActivate = async (
		credentials: UserLogin,
	): Promise<User | undefined> => {
		if (!isSignInLoaded || !signIn || !setActive) {
			return undefined;
		}

		const result = await signIn.create({
			identifier: credentials.username,
			password: credentials.password,
		});

		if (result.status === 'complete') {
			await setActive({ session: result.createdSessionId });

			const metaDataResp = await fetchUserMetaData();
			return metaDataResp.data.user;
		}

		return undefined;
	};

	const login = async (credentials: UserLogin) => {
		try {
			return await signInAndActivate(credentials);
		} catch (error: unknown) {
			if (
				isClerkAPIResponseError(error) &&
				error.errors[0]?.code === 'session_exists'
			) {
				await signOut();
				return await signInAndActivate(credentials);
			}
			throw error;
		}
	};

	const logout = async () => {
		await signOut();
	};

	return (
		<AuthContext.Provider
			value={{
				isLoaded: isLoaded && isSignInLoaded,
				isAuthenticated: !!isSignedIn,
				login,
				logout,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};
